/**
 * MARCA | 24/08/2026 | PDP upsell product – handles variant switching, price/image updates, and AJAX add-to-cart
 */

import { CartAddEvent } from "@theme/events";

class PdpUpsellProduct extends HTMLElement {
  constructor() {
    super();
    this.variants = [];
    this.currentVariant = null;
  }

  connectedCallback() {
    this.variantsJson = this.querySelector('[data-upsell-variants-json]');
    if (this.variantsJson) {
      try {
        this.variants = JSON.parse(this.variantsJson.textContent);
      } catch (e) {
        console.error('[PDP Upsell] Failed to parse variants JSON:', e);
        return;
      }
    }

    this.imageEl = this.querySelector('[data-upsell-image]');
    this.priceEl = this.querySelector('[data-upsell-price]');
    this.variantInput = this.querySelector('[data-upsell-variant-id]');
    this.addBtn = this.querySelector('[data-upsell-add-btn]');
    this.btnText = this.querySelector('[data-upsell-btn-text]');
    this.form = this.querySelector('form[data-type="add-to-cart-form"]');

    this.optionInputs = this.querySelectorAll('[data-option-position]');
    this.optionInputs.forEach((input) => {
      input.addEventListener('change', this.handleOptionChange.bind(this));
    });

    if (this.form) {
      this.form.addEventListener('submit', this.handleSubmit.bind(this));
    }

    this.setInitialVariant();
  }

  setInitialVariant() {
    const selectedOptions = this.getSelectedOptions();
    this.currentVariant = this.getVariantFromOptions(selectedOptions);
  }

  getSelectedOptions() {
    const options = {};
    this.optionInputs.forEach((input) => {
      if (input.checked) {
        options[input.dataset.optionPosition] = input.value;
      }
    });
    return options;
  }

  getVariantFromOptions(selectedOptions) {
    return this.variants.find((variant) => {
      return variant.options.every((optionValue, index) => {
        const position = String(index + 1);
        return selectedOptions[position] === optionValue;
      });
    });
  }

  handleOptionChange() {
    const selectedOptions = this.getSelectedOptions();
    const variant = this.getVariantFromOptions(selectedOptions);

    if (!variant) return;

    this.currentVariant = variant;
    this.updateVariantId(variant);
    this.updatePrice(variant);
    this.updateImage(variant);
    this.updateButtonState(variant);
    this.updateUnavailableOptions(selectedOptions);
  }

  updateVariantId(variant) {
    if (this.variantInput) {
      this.variantInput.value = variant.id;
      this.variantInput.disabled = !variant.available;
    }
  }

  updatePrice(variant) {
    if (!this.priceEl) return;

    const formatMoney = (cents) => {
      return (
        window.Shopify?.formatMoney?.(cents) ||
        new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: window.Shopify?.currency?.active || 'GBP',
          minimumFractionDigits: 2,
        }).format(cents / 100)
      );
    };

    let priceHtml = `<span class="pdp-upsell__price--current">${formatMoney(variant.price)}</span>`;
    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      priceHtml += ` <s class="pdp-upsell__price--compare">${formatMoney(variant.compare_at_price)}</s>`;
    }
    this.priceEl.innerHTML = priceHtml;
  }

  updateImage(variant) {
    if (!this.imageEl) return;

    const imageUrl = variant.featured_image?.src;
    if (imageUrl) {
      const sizedUrl = imageUrl.replace(/(\.[^.]+)$/, '_300x$1');
      this.imageEl.src = sizedUrl;
      this.imageEl.alt = variant.featured_image.alt || '';
    }
  }

  updateButtonState(variant) {
    if (!this.addBtn || !this.btnText) return;

    if (variant.available) {
      this.addBtn.disabled = false;
      this.btnText.textContent = 'Add to Cart';
    } else {
      this.addBtn.disabled = true;
      this.btnText.textContent = 'Sold Out';
    }

    this.addBtn.removeAttribute('data-added');
  }

  updateUnavailableOptions(selectedOptions) {
    const positionCount = Object.keys(selectedOptions).length;
    if (positionCount <= 1) return;

    this.optionInputs.forEach((input) => {
      const position = input.dataset.optionPosition;
      const testOptions = { ...selectedOptions, [position]: input.value };
      const matchingVariant = this.getVariantFromOptions(testOptions);
      const label = input.closest('.pdp-upsell__option-btn');

      if (label) {
        if (!matchingVariant || !matchingVariant.available) {
          label.classList.add('pdp-upsell__option-btn--unavailable');
        } else {
          label.classList.remove('pdp-upsell__option-btn--unavailable');
        }
      }
    });
  }

  async handleSubmit(event) {
    event.preventDefault();

    if (!this.currentVariant || !this.currentVariant.available) return;

    this.addBtn.setAttribute('data-adding', 'true');
    this.btnText.textContent = 'Adding...';

    try {
      const response = await fetch(window.Shopify.routes.root + "cart/add.js", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: [
            {
              id: this.currentVariant.id,
              quantity: 1,
              // MARCA | 09/10/2026 | Hidden line property so Complete the Look sales can be found on orders
              properties: { _upsell_source: "complete_the_look" },
            },
          ],
        }),
      });

      if (!response.ok) throw new Error("Add to cart failed");

      this.addBtn.removeAttribute("data-adding");
      this.addBtn.setAttribute("data-added", "true");
      this.btnText.textContent = "Added!";

      // MARCA | 30/10/2026 | Dispatch the theme's CartAddEvent (matches style-with.js) so the cart drawer, cart count and Halloween banner re-render. Nothing listened for the previous cart:refresh / product-form:submit events.
      const cartResponse = await fetch(window.Shopify.routes.root + "cart.js");
      const cartData = await cartResponse.json();

      document.dispatchEvent(
        new CartAddEvent(cartData, "pdp-upsell", {
          itemCount: cartData.item_count,
          source: "pdp-upsell",
        }),
      );

      setTimeout(() => {
        this.addBtn.removeAttribute("data-added");
        this.btnText.textContent = "Add to Cart";
      }, 2000);
    } catch (error) {
      console.error('[PDP Upsell] Add to cart error:', error);
      this.addBtn.removeAttribute('data-adding');
      this.btnText.textContent = 'Error - Retry';
      setTimeout(() => {
        this.btnText.textContent = 'Add to Cart';
      }, 3000);
    }
  }
}

if (!customElements.get('pdp-upsell-product')) {
  customElements.define('pdp-upsell-product', PdpUpsellProduct);
}
